<template>
  <b-dropdown class="custom-time-range-selector">
    <b-input
      slot="trigger"
      iconPack="fas"
      iconRight="caret-down"
      :value="formattedSelectedTimeRange"
      readonly/>
    <b-dropdown-item
      v-for="preset in timeRangePresets"
      :key="`time_range_${preset.value}`"
      @click="selectTimeRangePreset(preset)">
      {{ preset.label }}
    </b-dropdown-item>
    <b-dropdown-item
      aria-role="listitem"
      custom>
      {{ $t('navbar.timeRangeSelector.customRangeLabel') }}
      <b-slider
        v-model="localSelectedTimeRange"
        :min="timeRangeMinimum"
        :max="timeRangeMaximum"
        :step="timeRangeStep"
        :customFormatter="formatTime"
        rounded>
        <b-slider-tick
          v-for="tick in customTimeRangeTicks"
          :key="`custom_time_range_tick_${tick.value}`"
          :value="tick.value">
          {{ tick.label }}
        </b-slider-tick>
      </b-slider>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
import { INTERVAL, ONE_HOUR, ONE_MINUTE } from '@js/constants'
import { formatTime } from '@js/utils'

const TIME_RANGE_MINIMUM = 8 * ONE_HOUR // 8:00AM
const TIME_RANGE_MAXIMUM = 21 * ONE_HOUR + 30 * ONE_MINUTE // 9:30PM
const TIME_RANGE_2_30PM = 14 * ONE_HOUR + 30 * ONE_MINUTE // 2:30PM
const TIME_RANGE_6PM = 18 * ONE_HOUR // 6:00PM

export default {
  name: 'custom-time-range-selector',
  props: {
    selectedTimeRange: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      timeRangePresets: [
        { label: this.$t('navbar.timeRangeSelector.morningRangeLabel'), value: [TIME_RANGE_MINIMUM, TIME_RANGE_2_30PM] },
        { label: this.$t('navbar.timeRangeSelector.afternoonRangeLabel'), value: [TIME_RANGE_2_30PM, TIME_RANGE_6PM] },
        { label: this.$t('navbar.timeRangeSelector.eveningRangeLabel'), value: [TIME_RANGE_6PM, TIME_RANGE_MAXIMUM] }
      ],
      timeRangeMinimum: TIME_RANGE_MINIMUM,
      timeRangeMaximum: TIME_RANGE_MAXIMUM,
      timeRangeStep: INTERVAL,
      localSelectedTimeRange: null
    }
  },
  computed: {
    customTimeRangeTicks() {
      const ticks = []
      ticks.push(TIME_RANGE_MINIMUM)
      ticks.push(TIME_RANGE_2_30PM)
      ticks.push(TIME_RANGE_6PM)
      ticks.push(TIME_RANGE_MAXIMUM)

      return ticks.map(value => {
        return {
          label: formatTime(value),
          value
        }
      })
    },
    formattedSelectedTimeRange() {
      if (this.localSelectedTimeRange) {
        return `${formatTime(this.localSelectedTimeRange[0])}-${formatTime(this.localSelectedTimeRange[1])}`
      }
      return null
    }
  },
  watch: {
    localSelectedTimeRange() {
      this.$emit('update:selectedTimeRange', this.localSelectedTimeRange)
    }
  },
  created() {
    if (this.selectedTimeRange) {
      this.localSelectedTimeRange = this.selectedTimeRange
    } else if (!this.localSelectedTimeRange) {
      this.localSelectedTimeRange = this.timeRangePresets[0].value
    }
  },
  methods: {
    selectTimeRangePreset(preset) {
      this.localSelectedTimeRange = preset.value
    },
    formatTime(value) {
      return formatTime(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-time-range-selector {
  max-width: 160px;

  &::v-deep {
    .input {
      cursor: pointer;
    }

    .icon {
      color: $primary;
    }

    .dropdown-content {
      width: 400px;
    }

    .dropdown-item:not(:last-child) {
      border-bottom: 1px solid $grey-lighter;
    }
  }

  .b-slider {
    &::v-deep {
      .b-slider-tick {
        &:nth-child(2) .b-slider-tick-label {
          transform: translateX(-10%);
        }

        &:nth-last-child(3) .b-slider-tick-label {
          transform: translateX(-90%);
        }

        .b-slider-tick-label {
          margin-top: $space-xxs;
          font-size: 10px;
        }
      }
    }
  }
}
</style>
